/* Pastille d'ajout rapide (snippets/product-tile-quick-add.liquid).
 *
 * Deux roles :
 *   1. Le feedback visuel de l'ajout : spinner -> coche -> tiroir du panier.
 *   2. La mini-modale de choix de saveur des produits a plusieurs variantes.
 *
 * ---------------------------------------------------------------------------
 * 1. POURQUOI RETARDER LE TIROIR
 *
 * Le theme ouvre le cart drawer des la reponse de /cart/add.js : ModalCart
 * (global.bundle.js) ecoute `shapes:cart:afteradditem` sur document.body et
 * appelle aussitot Alpine.store('modals').open('cart'). Resultat sans ce
 * script : clic -> tiroir, sans rien entre les deux.
 *
 * On ecoute donc l'evenement en phase de CAPTURE sur `window`. L'evenement etant
 * dispatche sur document.body, le chemin de propagation est
 * window -> document -> html -> body : notre listener passe AVANT tous ceux du
 * theme. On coupe la propagation, puis on redispatche un evenement identique
 * apres le delai. Tous les abonnes (ModalCart, compteur, cart-items,
 * cart-footer, barre de livraison offerte) le recoivent normalement, juste
 * 450 ms plus tard — donc en phase avec l'animation.
 *
 * Seuls les ajouts venant d'une pastille sont retardes : la page produit et le
 * configurateur gardent leur comportement immediat.
 *
 * ---------------------------------------------------------------------------
 * 2. LA MODALE DE SAVEUR
 *
 * Elle est deplacee dans <body> au chargement : laissee dans la tuile, elle
 * serait prisonniere d'un ancetre transforme (les carrousels de recommandations
 * posent un `transform` sur leur piste, ce qui casse `position: fixed` pour tout
 * descendant). Ses champs restent rattaches au formulaire de l'island par
 * l'attribut HTML `form=` — le formulaire, lui, ne bouge pas, parce que
 * submitForm() le retrouve avec `$root.querySelector('.quick-buy-product-form')`.
 */
(function () {
  var DELAY = 450; // doit rester >= la transition de la coche (0.18s) + un temps de lecture
  var ADDED_MS = 1200; // duree d'affichage de la coche apres l'ouverture du tiroir
  var ERROR_MS = 2600;

  var pendingEl = null; // pastille OU bouton de modale dont l'ajout est en cours
  var pendingFormId = null; // formulaire a l'origine de cet ajout
  var pendingModal = null; // modale a refermer une fois l'ajout passe
  var replaying = false; // vrai pendant notre propre redispatch, pour ne pas boucler
  var lastOpener = null; // element a refocaliser a la fermeture

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------------------------------------------------------------------
   * Modale : mise en place
   * ------------------------------------------------------------------- */

  /* Une modale par produit multi-variantes, mais la meme tuile peut etre rendue
     deux fois sur une page (grille + recommandations) : on ne garde que la
     premiere, sinon deux dialogues partageraient le meme id. */
  function teleportModals() {
    var seen = {};
    Array.prototype.forEach.call(document.querySelectorAll('[data-qa-modal]'), function (modal) {
      if (seen[modal.id]) {
        modal.parentNode.removeChild(modal);
        return;
      }
      seen[modal.id] = true;
      document.body.appendChild(modal);
    });
  }

  function focusablesIn(modal) {
    return Array.prototype.filter.call(
      modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])'),
      function (el) {
        return el.offsetParent !== null || el.classList.contains('qa-flavor__input');
      }
    );
  }

  function openModal(modal, opener) {
    lastOpener = opener || null;
    modal.hidden = false;
    document.body.classList.add('qa-modal-open');

    /* Deux images consecutives : la premiere applique l'etat ferme (opacite 0),
       la seconde declenche la transition. Sans ce sas, le navigateur fusionne
       les deux styles et la modale apparait sans animation. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        modal.classList.add('is-open');
      });
    });

    var checked = modal.querySelector('.qa-flavor__input:checked');
    var target = checked || modal.querySelector('.qa-flavor__input:not([disabled])');
    if (target) {
      target.focus({ preventScroll: true });
    } else {
      var close = modal.querySelector('[data-qa-close]');
      if (close) close.focus({ preventScroll: true });
    }
  }

  function closeModal(modal) {
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('qa-modal-open');

    var done = function () {
      modal.hidden = true;
    };
    if (reduceMotion()) {
      done();
    } else {
      setTimeout(done, 260); // duree de la transition du panneau
    }

    if (lastOpener && document.contains(lastOpener)) {
      lastOpener.focus({ preventScroll: true });
    }
    lastOpener = null;
  }

  function openModals() {
    return Array.prototype.filter.call(document.querySelectorAll('[data-qa-modal]'), function (m) {
      return !m.hidden;
    });
  }

  /* Ouverture : l'ouvreur est un vrai lien vers la fiche produit, on ne le
     neutralise que si la modale existe vraiment. */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-qa-open]');
    if (opener) {
      var modal = document.getElementById(opener.getAttribute('data-qa-open'));
      if (modal) {
        e.preventDefault();
        openModal(modal, opener);
      }
      return;
    }

    var closer = e.target.closest('[data-qa-close]');
    if (closer) {
      e.preventDefault();
      closeModal(closer.closest('[data-qa-modal]'));
    }
  });

  document.addEventListener('keydown', function (e) {
    var open = openModals()[0];
    if (!open) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal(open);
      return;
    }

    /* Piege a focus : sans lui, la tabulation repart dans la grille situee
       derriere le voile, que le lecteur d'ecran annonce toujours. */
    if (e.key === 'Tab') {
      var items = focusablesIn(open);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* Choix d'une saveur : le bouton s'active et annonce ce qui sera ajoute. */
  document.addEventListener('change', function (e) {
    var radio = e.target.closest('.qa-flavor__input');
    if (!radio) return;

    var modal = radio.closest('[data-qa-modal]');
    if (!modal) return;

    var cta = modal.querySelector('[data-qa-cta]');
    var label = modal.querySelector('[data-qa-cta-label]');
    var choice = modal.querySelector('[data-qa-choice]');

    if (cta) cta.disabled = false;
    if (label) label.textContent = 'Ajouter au panier';
    if (choice) choice.textContent = radio.getAttribute('data-qa-label') || '';
  });

  /* ---------------------------------------------------------------------
   * Ajout au panier : feedback + retard du tiroir
   * ------------------------------------------------------------------- */

  /* Le bouton a animer n'est pas toujours dans le formulaire : pour un produit
     multi-variantes, c'est celui de la modale, rattache a distance par `form=`. */
  function feedbackElementFor(form) {
    var inForm = form.querySelector('.product-quick-add');
    if (inForm) return inForm;

    /* getAttribute et pas `form.id` : les radios de saveur s'appellent name="id",
       et un champ nomme `id` masque la propriete `form.id` (elle renvoie alors
       la RadioNodeList). C'est ce qui laissait la modale ouverte apres l'ajout. */
    var formId = form.getAttribute('id');
    var match = null;
    Array.prototype.forEach.call(document.querySelectorAll('[data-qa-cta]'), function (cta) {
      if (!match && formId && cta.getAttribute('form') === formId) match = cta;
    });
    return match;
  }

  /* Capture : on passe avant le listener submit pose par island-quick-buy sur le
     formulaire lui-meme, donc l'etat est arme avant meme le fetch. */
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.classList || !form.classList.contains('product-quick-add-form')) return;

      pendingEl = feedbackElementFor(form);
      pendingModal = pendingEl ? pendingEl.closest('[data-qa-modal]') : null;
      pendingFormId = form.getAttribute('id') || null;

      /* Alpine pose `.is-loading` sur la pastille via `:class`, mais la modale
         est hors de sa portee depuis la teleportation : on s'en charge ici. */
      if (pendingEl && pendingEl.hasAttribute('data-qa-cta')) {
        pendingEl.classList.add('is-loading');
        pendingEl.disabled = true;
      }
    },
    true
  );

  /* Un ajout refuse (variante epuisee entre l'affichage et le clic) ne doit pas
     laisser l'etat arme : le prochain ajout, venu d'ailleurs, serait retarde et
     marque sur le mauvais bouton. */
  function clearPending() {
    pendingEl = null;
    pendingModal = null;
    pendingFormId = null;
  }

  /* Une erreur venue d'un autre formulaire ne doit pas consommer l'etat de la
     pastille en cours (audit A05). Pas de filtre equivalent sur le succes : un
     controle par variante (2026-09-25) empechait la modale de saveur de se
     refermer apres l'ajout, retire le 2026-09-26. */
  function onAddError(e) {
    var sourceId = e && e.detail ? e.detail.sourceId : null;
    if (sourceId && pendingFormId && sourceId !== pendingFormId) return;

    var el = pendingEl;
    var modal = pendingModal;
    clearPending();

    if (!el || !el.hasAttribute('data-qa-cta')) return;

    el.classList.remove('is-loading');
    el.disabled = false;

    var label = modal ? modal.querySelector('[data-qa-cta-label]') : null;
    if (!label) return;
    var previous = label.textContent;
    label.textContent = 'Ajout impossible — réessayez';
    setTimeout(function () {
      label.textContent = previous;
    }, ERROR_MS);
  }

  document.body.addEventListener('shapes:cart:adderror', onAddError);
  window.addEventListener('shapes:cart:adderror', onAddError);

  window.addEventListener(
    'shapes:cart:afteradditem',
    function (e) {
      if (replaying || !pendingEl) return;

      var el = pendingEl;
      var modal = pendingModal;
      clearPending();

      var detail = e.detail;
      e.stopPropagation();

      el.classList.remove('is-loading');
      el.classList.add('is-added');

      var wait = reduceMotion() ? 0 : DELAY;
      setTimeout(function () {
        if (modal) closeModal(modal);

        replaying = true;
        try {
          document.body.dispatchEvent(
            new CustomEvent('shapes:cart:afteradditem', { bubbles: true, detail: detail })
          );
        } finally {
          /* Remis a false dans un `finally` : si un abonne du theme jette, le flag
             resterait bloque a true et tous les ajouts suivants seraient retardes
             une seconde fois (donc jamais transmis). */
          replaying = false;
        }

        setTimeout(function () {
          el.classList.remove('is-added');
          if (el.hasAttribute('data-qa-cta')) {
            /* La modale repart d'une feuille blanche : sans ca, elle rouvrirait
               avec la saveur precedente encore cochee et un bouton actif. */
            el.disabled = true;
            var host = el.closest('[data-qa-modal]');
            if (!host) return;
            var label = host.querySelector('[data-qa-cta-label]');
            var choice = host.querySelector('[data-qa-choice]');
            if (label) label.textContent = 'Choisissez une saveur';
            if (choice) choice.textContent = 'Aucune saveur choisie';
            Array.prototype.forEach.call(host.querySelectorAll('.qa-flavor__input'), function (r) {
              r.checked = false;
            });
          }
        }, ADDED_MS);
      }, wait);
    },
    true // CAPTURE sur window : seule position qui precede les listeners du theme
  );

  teleportModals();
})();
